/**
 * Core Drawer / Sidebar Panel Component
 *
 * Slide-in panel from any edge with size variants, header/footer,
 * backdrop control, and overlay mode. Built on MUI Drawer.
 *
 * @example
 *   <Drawer open={open} onClose={close} title="Filters" anchor="right" size="sm">
 *     <FilterForm />
 *     <DrawerFooter>
 *       <Button variant="ghost" onClick={close}>Cancel</Button>
 *       <Button onClick={apply}>Apply Filters</Button>
 *     </DrawerFooter>
 *   </Drawer>
 */
import React from 'react';
import { Drawer as MuiDrawer, IconButton, Typography, Divider, Box } from '@mui/material';
import { X } from 'lucide-react';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Slide direction */
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  /** Title */
  title?: React.ReactNode;
  /** Subtitle */
  subtitle?: string;
  /** Header icon */
  icon?: React.ReactNode;
  /** Size preset */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Disable backdrop click close */
  disableBackdropClose?: boolean;
  /** Hide the close button */
  hideCloseButton?: boolean;
  /** Show header divider */
  headerDivider?: boolean;
  children: React.ReactNode;
  className?: string;
}

export interface DrawerFooterProps {
  children: React.ReactNode;
  className?: string;
}

const sizeMap: Record<string, number | string> = {
  xs: 280,
  sm: 360,
  md: 480,
  lg: 640,
  xl: 800,
  full: '100%',
};

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  anchor = 'right',
  title,
  subtitle,
  icon,
  size = 'sm',
  disableBackdropClose = false,
  hideCloseButton = false,
  headerDivider = true,
  children,
  className = '',
}) => {
  const isHorizontal = anchor === 'left' || anchor === 'right';
  const paperSx = isHorizontal
    ? { width: sizeMap[size], maxWidth: '100vw' }
    : { height: sizeMap[size], maxHeight: '100vh' };

  return (
    <MuiDrawer
      anchor={anchor}
      open={open}
      onClose={disableBackdropClose ? undefined : onClose}
      PaperProps={{ className, sx: { ...paperSx, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      {(title || !hideCloseButton) && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2.5, pr: 6, flexShrink: 0 }}>
            {icon && <span className="flex items-center mt-0.5 text-slate-400">{icon}</span>}
            <div className="flex-1 min-w-0">
              {typeof title === 'string' ? (
                <Typography variant="h6" fontWeight={700} noWrap>{title}</Typography>
              ) : title}
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>
              )}
            </div>
            {!hideCloseButton && (
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ position: 'absolute', right: 12, top: 12 }}
                aria-label="Close drawer"
              >
                <X className="h-4 w-4" />
              </IconButton>
            )}
          </Box>
          {headerDivider && <Divider />}
        </>
      )}

      {/* Content — scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
        {children}
      </Box>
    </MuiDrawer>
  );
};

export const DrawerFooter: React.FC<DrawerFooterProps> = ({ children, className = '' }) => (
  <>
    <Divider />
    <Box className={className} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2.5, flexShrink: 0 }}>
      {children}
    </Box>
  </>
);

export default Drawer;
